<script setup lang="ts">
import ScaleMenuState from "@/stores/ScaleMenuState";
import { trackSelection } from "@/analytics/drawing-analytics";
import { ref, watch, onMounted, onBeforeUnmount } from "vue";
import { Facade } from "@/drawing";
import type { ScaleConfig } from "@/types/legend.types";
import ScaleItem from "../DrawerMenus/Settings/ScaleItem.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

const props = defineProps<{
  target: string;
}>();

const input = ref<number>();
const size = ref<number | string>("-");

function refreshSize() {
  size.value = Facade.isInitialized
    ? (Facade.photoMarkers.getSelectedTextHeight() ?? "-")
    : "-";
}

watch(() => props.target, refreshSize, { immediate: true });

onMounted(() => {
  if (Facade.isInitialized) Facade.history.onChange(refreshSize);
});

onBeforeUnmount(() => {
  if (Facade.isInitialized) Facade.history.offChange(refreshSize);
});

function applyScale(config: ScaleConfig) {
  trackSelection("photo_marker_size_changed");
  if (!Facade.isInitialized) return;
  const height = config.props.textsize ?? config.props.textstyle?.size;
  if (height) {
    Facade.photoMarkers.setSelectedTextHeight(height);
    refreshSize();
  }
}

function setHeight() {
  trackSelection("photo_marker_size_changed");
  if (input.value && Facade.isInitialized) {
    Facade.photoMarkers.setSelectedTextHeight(input.value);
    refreshSize();
  }
}
</script>

<template>
  <button
    type="button"
    class="canvas-btn canvas-btn-nosize dropdown-toggle scale-btn"
    data-bs-toggle="dropdown"
    aria-expanded="false"
  >
    h({{ size }})
  </button>
  <ul
    class="dropdown-menu canvas-dropdown overflow-y-auto"
    style="max-height: 45vh"
  >
    <li>
      <div class="input-group p-1">
        <input
          type="number"
          inputmode="decimal"
          enterkeyhint="done"
          class="form-control"
          v-model="input"
          style="width: 50px"
          @keydown.enter.prevent="
            setHeight();
            ($event.target as HTMLInputElement).blur();
          "
        />
        <span
          type="button"
          class="input-group-text"
          aria-expanded="false"
          @click="setHeight()"
        >
          <PhosphorIcon name="check" :size="14" />
        </span>
      </div>
    </li>
    <li>
      <h6 class="dropdown-header">
        {{ $t("canvas:floating.photomarker.size.header") }}
      </h6>
    </li>
    <li
      v-for="scale in ScaleMenuState.scales"
      :key="scale.name"
      @click="() => applyScale(scale)"
    >
      <ScaleItem :scale="scale" />
    </li>
  </ul>
</template>

<style lang="scss" scoped>
// Toute la hauteur de la barre, comme les autres boutons.
.scale-btn {
  height: 36px;
  padding: 0 10px;
  gap: 4px;
}

.canvas-dropdown {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  color: white;

  .dropdown-header {
    color: rgba(255, 255, 255, 0.4);
  }

  :deep(.dropdown-item) {
    color: white;
    &:hover,
    &:focus {
      background-color: rgba(255, 255, 255, 0.08);
      color: white;
    }
    &.active {
      background-color: var(--canvas-btn-active-bg);
      color: white;
    }
  }

  .input-group-text {
    background-color: var(--canvas-bg-hover);
    border-color: var(--canvas-border);
    color: white;
    cursor: pointer;
    // Survol à la souris seulement (au doigt, :hover reste collé après l'appui).
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        background-color: var(--canvas-btn-active-bg);
      }
    }
  }

  .form-control {
    background-color: var(--canvas-bg-hover);
    border-color: var(--canvas-border);
    color: white;
    &:focus {
      background-color: var(--canvas-bg-hover) !important;
      color: white !important;
      box-shadow: none;
      border-color: var(--canvas-border);
    }
  }
}
</style>
